/**
 * KaTeX rendering wrapper.
 *
 * Centralizes math typesetting so the rest of the UI never touches KaTeX
 * directly. Rendering is defensive: on any KaTeX error we fall back to the raw
 * source in a <code> element rather than throwing, so a display glitch can
 * never break a practice session.
 */

import katex from "katex";
import "katex/dist/katex.min.css";

export function renderMathInto(el: HTMLElement, latex: string, display = true): void {
  try {
    katex.render(latex, el, {
      displayMode: display,
      throwOnError: false,
      output: "htmlAndMathml",
    });
  } catch {
    el.textContent = latex;
  }
}

/** Create a new element containing rendered math. */
export function mathElement(latex: string, display = true): HTMLElement {
  const span = document.createElement(display ? "div" : "span");
  span.className = "math";
  renderMathInto(span, latex, display);
  return span;
}

/** Render inline math to an HTML string (for insertion into templates). */
export function mathToHtml(latex: string, display = false): string {
  try {
    return katex.renderToString(latex, {
      displayMode: display,
      throwOnError: false,
      output: "htmlAndMathml",
    });
  } catch {
    return `<code>${escapeHtml(latex)}</code>`;
  }
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
